"use client";

import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP);
export default function LocalAuthorityHero() {
  const container = useRef<HTMLDivElement>(null);
  const [time, setTime] = useState<string>("");
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Europe/London",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false
      });
      setTime(formatter.format(now));
    };
    updateTime();
    const interval = setInterval(updateTime, 30000);
    return () => clearInterval(interval);
  }, []);
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      const tl = gsap.timeline({
        defaults: {
          ease: "power3.out"
        }
      });
      tl.from(".conv-kicker", {
        opacity: 0,
        y: 16,
        duration: 0.7
      }).from(".conv-line", {
        opacity: 0,
        y: 42,
        duration: 0.9,
        stagger: 0.09,
        ease: "cubic-bezier(0.2, 0.7, 0.15, 1)" as unknown as string
      }, "-=0.4").from(".marker-path", {
        drawLength: 1,
        duration: 0.62,
        ease: "power2.out"
      }, "-=0.15").from(".conv-footer", {
        opacity: 0,
        y: 24,
        duration: 0.8,
        stagger: 0.12
      }, "-=0.3");
      const path = container.current?.querySelector(".marker-path") as SVGPathElement | null;
      if (path) {
        const length = path.getTotalLength();
        path.style.strokeDasharray = `${length}`;
        path.style.strokeDashoffset = `${length}`;
        gsap.to(path, {
          strokeDashoffset: 0,
          duration: 0.9,
          delay: 1.1,
          ease: "power2.out"
        });
      }
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".conv-kicker", ".conv-line", ".conv-footer"], {
        opacity: 1,
        y: 0
      });
      const path = container.current?.querySelector(".marker-path") as SVGPathElement | null;
      if (path) {
        path.style.strokeDasharray = "none";
        path.style.strokeDashoffset = "0";
      }
    });
  }, {
    scope: container
  });
  return <section data-section-id="23130" id="localauthorityhero" data-nav="light" className="relative w-full bg-background text-foreground overflow-hidden" style={{
    paddingTop: "clamp(9rem, 16vh, 13rem)",
    paddingBottom: "clamp(7rem, 14vh, 11rem)"
  }}>
      <div ref={container} className="mx-auto w-full" style={{
      maxWidth: "1440px",
      paddingLeft: "clamp(1.5rem, 4vw, 4.5rem)",
      paddingRight: "clamp(1.5rem, 4vw, 4.5rem)"
    }}>
        <div className="grid grid-cols-12 gap-x-6 md:gap-x-8">
          {}
          <div className="col-span-12 md:col-span-3">
            <div className="conv-kicker flex flex-col gap-4">
              <span className="font-sans font-medium uppercase text-muted-foreground" style={{
              fontSize: "clamp(0.8rem, 0.85vw, 0.9375rem)",
              letterSpacing: "0.16em",
              lineHeight: 1.2
            }}>
                {"CONSULT \u00B7 REPLY WITHIN 4HR"}
              </span>
              <span className="block h-px bg-border" style={{
              width: "40px"
            }} aria-hidden="true" />
            </div>
          </div>

          {}
          <div className="col-span-12 md:col-span-11 mt-14 md:mt-14 md:col-start-1">
            <h1 className="font-serif text-foreground" style={{
            fontSize: "clamp(3.75rem, 10.5vw, 11rem)",
            fontWeight: 400,
            letterSpacing: "-0.035em",
            lineHeight: 0.92
          }}>
              <span className="conv-line block overflow-hidden">
                <span className="block">{"Let me help you"}</span>
              </span>
              <span className="conv-line block overflow-hidden">
                <span className="block">{"scale your search"}</span>
              </span>
              <span className="conv-line block overflow-hidden">
                <span className="block">
                  {"visibility "}
                  <span className="relative inline-block italic">
                    <span className="relative z-10">{"today."}</span>
                    <svg aria-hidden="true" className="absolute left-0 right-0 pointer-events-none" style={{
                    bottom: "0.08em",
                    width: "100%",
                    height: "0.35em",
                    zIndex: 1
                  }} viewBox="0 0 300 40" preserveAspectRatio="none">
                      <path className="marker-path" d="M4 26 C 40 18, 90 30, 140 22 C 190 15, 240 28, 296 20" stroke="hsl(340 75% 65%)" strokeWidth="14" strokeLinecap="round" fill="none" opacity="0.55" />
                    </svg>
                  </span>
                </span>
              </span>
            </h1>
          </div>
        </div>

        {}
        <div className="grid grid-cols-12 gap-x-6 md:gap-x-8" style={{
        marginTop: "clamp(3rem, 6vw, 4.5rem)"
      }}>
          {}
          <div className="conv-footer col-span-12 md:col-span-7">
            <p className="font-sans text-foreground max-w-2xl" style={{
            fontSize: "clamp(1.25rem, 1.55vw, 1.5rem)",
            fontWeight: 400,
            letterSpacing: "-0.005em",
            lineHeight: 1.55
          }}>
              {"I provide hands-on SEO strategy for ambitious businesses. If your project is urgent, mention it in your email and I will respond today."}
            </p>
          </div>

          {}
          <div className="conv-footer col-span-12 md:col-span-5 mt-10 md:mt-0">
            <div className="flex flex-col gap-4">
              <span className="font-sans font-medium uppercase text-muted-foreground" style={{
              fontSize: "clamp(0.8rem, 0.85vw, 0.9375rem)",
              letterSpacing: "0.16em",
              lineHeight: 1.2
            }}>
                {"DIRECT"}
              </span>

              <Link to="/ContactPage" className="group relative inline-block w-fit font-serif italic text-foreground transition-colors duration-200 hover:text-accent" style={{
              fontSize: "clamp(1.375rem, 2vw, 1.875rem)",
              fontWeight: 400,
              letterSpacing: "-0.02em",
              lineHeight: 1.1
            }}>
                <span className="relative z-10">{"contact@ye-consultancy.com"}</span>
                <span aria-hidden="true" className="absolute left-0 -bottom-1 h-[2px] w-0 bg-accent transition-all duration-300 ease-out group-hover:w-full" />
              </Link>

              <p className="font-sans text-foreground" style={{
              fontSize: "clamp(1.125rem, 1.2vw, 1.25rem)",
              fontWeight: 400,
              lineHeight: 1.6
            }}>
                {"+44 7490 984430"}
              </p>

              <p className="font-sans text-muted-foreground" style={{
              fontSize: "clamp(0.9375rem, 0.95vw, 1rem)",
              letterSpacing: "0.01em",
              lineHeight: 1.45
            }}>
                {`YE CONSULTANCY \u00B7 ${time || "--:--"} GMT`}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}